import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", city: "", car_year: "", car_color: "", plate_number: "", message: "" };

const inputCls = "border-white/10 bg-[#0B0D15] text-slate-200 placeholder:text-slate-500 focus-visible:ring-rose-500/50";

export default function RegisterPage() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Pendaftaran terkirim! Pengurus akan menghubungimu via WhatsApp.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Navbar />
      <main className="mx-auto max-w-3xl space-y-10 px-4 pb-24 pt-32 sm:px-6">
        <SectionHeading label="Pendaftaran Anggota" title="Gabung 3C Chapter Jatim"
          desc="Khusus pemilik Chevrolet Captiva yang berdomisili di Jawa Timur. Isi data di bawah, pengurus akan menghubungimu untuk kopdar perdana." />
        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="registration-success-message"
            className="space-y-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-10 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h3 className="font-heading text-3xl font-bold uppercase text-white">Pendaftaran Diterima</h3>
            <p className="text-slate-300">Terima kasih! Pengurus chapter akan segera menghubungimu via WhatsApp. Sampai jumpa di kopdar!</p>
            <Button data-testid="registration-again-button" onClick={() => setDone(false)} variant="outline"
              className="border-white/15 text-slate-200 hover:bg-white/5">
              Daftarkan Anggota Lain
            </Button>
          </motion.div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            data-testid="member-registration-form" onSubmit={submit}
            className="grid gap-5 rounded-xl border border-white/10 bg-[#12141C]/90 p-6 shadow-2xl shadow-black/60 sm:grid-cols-2 sm:p-8">
            <div className="space-y-2">
              <Label htmlFor="reg-name" className="text-slate-300">Nama Lengkap *</Label>
              <Input id="reg-name" data-testid="reg-name-input" required value={form.name} onChange={set("name")} placeholder="Nama sesuai KTP" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-phone" className="text-slate-300">No. WhatsApp *</Label>
              <Input id="reg-phone" data-testid="reg-phone-input" required value={form.phone} onChange={set("phone")} placeholder="08xx-xxxx-xxxx" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email" className="text-slate-300">Email</Label>
              <Input id="reg-email" data-testid="reg-email-input" type="email" value={form.email} onChange={set("email")} placeholder="email@contoh.com" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-city" className="text-slate-300">Kota / Kabupaten</Label>
              <Input id="reg-city" data-testid="reg-city-input" value={form.city} onChange={set("city")} placeholder="Surabaya, Malang, Kediri..." className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Tipe Captiva</Label>
              <Select value={form.car_year} onValueChange={set("car_year")}>
                <SelectTrigger data-testid="reg-year-select" className={inputCls}>
                  <SelectValue placeholder="Pilih tipe Captiva" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#11131F] text-slate-200">
                  <SelectItem value="LT">LT</SelectItem>
                  <SelectItem value="LTZ">LTZ</SelectItem>
                  <SelectItem value="AWD">AWD</SelectItem>
                  <SelectItem value="NFL">NFL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-color" className="text-slate-300">Warna Mobil</Label>
              <Input id="reg-color" data-testid="reg-color-input" value={form.car_color} onChange={set("car_color")} placeholder="Hitam, putih, silver..." className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-plate" className="text-slate-300">No. Polisi</Label>
              <Input id="reg-plate" data-testid="reg-plate-input" value={form.plate_number} onChange={set("plate_number")} placeholder="L 1234 ABC" className={inputCls} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="reg-message" className="text-slate-300">Pesan / Alasan Bergabung</Label>
              <Textarea id="reg-message" data-testid="reg-message-input" rows={4} value={form.message} onChange={set("message")}
                placeholder="Ceritakan singkat tentang kamu dan Captiva-mu..." className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="member-registration-submit-button" type="submit" disabled={saving}
                className="w-full bg-rose-600 py-6 text-base font-bold text-white shadow-lg shadow-rose-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-rose-500 hover:shadow-rose-500/50">
                {saving ? "Mengirim..." : "Kirim Pendaftaran"}
              </Button>
            </div>
          </motion.form>
        )}
      </main>
      <Footer />
    </div>
  );
}
